import { useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, apiError } from "@/lib/api";
import { StarInput } from "@/components/fleet/Stars";

const REASONS = ["Volo cancellato", "Imprevisto", "Errore di prenotazione", "Altro"];

const ActionDialog = ({ testid, title, description, children, onClose }) => (
  <Dialog open onOpenChange={(o) => !o && onClose()}>
    <DialogContent data-testid={testid} className="ff-glass-panel sm:max-w-md max-h-[92vh] overflow-y-auto rounded-2xl p-5 sm:p-7">
      <DialogHeader className="text-left">
        <DialogTitle className="ff-display text-xl font-extrabold">{title}</DialogTitle>
        <DialogDescription className="text-[#94A3B8] text-sm">{description}</DialogDescription>
      </DialogHeader>
      {children}
    </DialogContent>
  </Dialog>
);

const useSubmit = (fn, ok, onDone) => {
  const [busy, setBusy] = useState(false);
  const run = async (e) => {
    e?.preventDefault();
    setBusy(true);
    try {
      await fn();
      toast.success(ok);
      onDone();
    } catch (err) {
      toast.error(apiError(err));
      setBusy(false);
    }
  };
  return [busy, run];
};

export const CancelDialog = ({ ride, onClose, onDone }) => {
  const [reason, setReason] = useState(REASONS[0]);
  const [note, setNote] = useState("");
  const direct = ride.status === "available";
  const [busy, run] = useSubmit(() => api.post(`/rides/${ride.id}/cancel`, { reason, note }),
    direct ? "Corsa ritirata dalla bacheca" : "Richiesta inviata al collega", onDone);
  return (
    <ActionDialog testid="cancel-dialog" onClose={onClose} title={direct ? "Ritira corsa" : "Richiedi annullamento"}
      description={direct ? "La corsa non è ancora prenotata: verrà tolta subito dalla bacheca." : "Il collega può accettare (rimborso automatico dall'Escrow) o rifiutare: in quel caso decide l'assistenza FLEETFLOW."}>
      <form onSubmit={run} className="flex flex-col gap-4">
        <div className="flex flex-wrap gap-2">
          {REASONS.map((r) => (
            <button key={r} type="button" data-testid={`cancel-reason-${r.toLowerCase().replace(/\s+/g, "-")}`} data-active={reason === r} onClick={() => setReason(r)} className="ff-chip">{r}</button>
          ))}
        </div>
        <textarea data-testid="cancel-note-input" maxLength={300} rows={3} className="ff-field !h-auto py-3" placeholder="Dettagli (es. volo AZ123 cancellato)" value={note} onChange={(e) => setNote(e.target.value)} />
        <button type="submit" data-testid="cancel-submit-button" disabled={busy} className="ff-btn-secondary w-full uppercase text-sm border-red-500 text-red-300">{busy ? "Invio…" : direct ? "Ritira corsa" : "Invia richiesta di annullamento"}</button>
      </form>
    </ActionDialog>
  );
};

export const ReviewDialog = ({ ride, onClose, onDone }) => {
  const [form, setForm] = useState({ punctuality: 5, cleanliness: 5, professionalism: 5, comment: "" });
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const [busy, run] = useSubmit(() => api.post(`/rides/${ride.id}/review`, form), "Recensione pubblicata", onDone);
  return (
    <ActionDialog testid="review-dialog" onClose={onClose} title="Valuta il collega" description={`${ride.origin} ➔ ${ride.destination}`}>
      <form onSubmit={run} className="flex flex-col gap-3">
        <StarInput label="Puntualità" value={form.punctuality} onChange={set("punctuality")} testid="review-punctuality" />
        <StarInput label="Pulizia del mezzo" value={form.cleanliness} onChange={set("cleanliness")} testid="review-cleanliness" />
        <StarInput label="Professionalità" value={form.professionalism} onChange={set("professionalism")} testid="review-professionalism" />
        <textarea data-testid="review-comment-input" maxLength={500} rows={3} className="ff-field !h-auto py-3" placeholder="Commento (facoltativo)" value={form.comment} onChange={(e) => set("comment")(e.target.value)} />
        <button type="submit" data-testid="review-submit-button" disabled={busy} className="ff-btn-primary w-full uppercase text-sm">{busy ? "Invio…" : "Pubblica recensione"}</button>
      </form>
    </ActionDialog>
  );
};
